import { findBranchContainingStep } from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer.helpers";
import { UpdateRecordStatusFormValuesType } from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationFormStore";
import {
  AssessTemplatedReportForCommentsAction,
  ChoiceTask,
  CreateEventAction,
  CreateFormActionType,
  DelayActionType,
  FormChoiceAction,
  FormSubmissionTaskType,
  isTriggerAssetCreate,
  isTriggerAssetUpdate,
  RunCalculationsAction,
  RunDRVCActionType,
  RunTemplatedReportAction,
  RunWorkerActionType,
  SendNotificationActionType,
  SendReportActionType,
  SystemActionType,
  UpdateAttributesActionType,
  UserTaskType,
  VariableChoiceAction,
  WorkflowConfigStepType,
  WorkflowConfigurationType,
  WorkflowPluginConfig,
  WorkflowStepType,
  WorkflowSystemActionType,
  WorkflowTriggerAssetCreate,
  WorkflowTriggerAssetUpdate,
  WorkflowTriggerDisplayType,
  WorkflowTriggerDueDate,
  WorkflowTriggerFormCreate,
  WorkflowTriggerScheduled,
  WorkflowTriggerType,
  WorkflowTriggerWorkflowComplete,
  WorkflowUserTaskType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import {
  applyEdgeChanges,
  applyNodeChanges,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import { cloneDeep, pick } from "es-toolkit/compat";
import { produce } from "immer";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { WorkflowGraphParallelNodeType } from "../ParallelGraphNode/types";
import { WorkflowGraphEdgeType } from "../WorkflowGraphEdge/types";
import { WorkflowGraphNodeType } from "../WorkflowGraphNode/types";
import { deleteStepFromWorkflow } from "./helpers/deleteWorkflowGraphStep.helpers";
import {
  collectParentPathsForStep,
  getAvailableVariablesForStep,
  getWorkflowConfigurationTriggerIndexFromTriggerKey,
  getWorkflowConfigurationTriggerKeyFromIndex,
} from "./useWorkflowGraph.helpers";

const { flattenWorkflowSteps, getAllWorkflowSteps } = Workflows.logic;

type NodeId = string;

type WorkflowGraphStoreState = {
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
  isFetchingConfiguration: boolean;
  originalConfiguration?: WorkflowConfigurationType;
  originalTriggers?: WorkflowTriggerType[];
  currentConfiguration?: WorkflowConfigurationType;
  currentTriggers?: WorkflowTriggerType[];
  templateVariables: string[];
  taskVariables: Record<string, { input: string[]; output: string[] }>;
  triggerVariables: string[];
  initialVariables: string[];
  pathMap: Record<string, string[]>;
  reversePathMap: Record<string, string[]>;
  dirtyNodes: Record<NodeId, boolean>;
  selectedNodeId?: NodeId;
  edges: WorkflowGraphEdgeType[];
  isInEditMode: boolean;
  isSelectingNodeToSourceFormDataFrom: boolean;
  nodeIdToSourceFormDataFrom: string | undefined;
  isDummyNodeSelected: boolean;
  nextStepIdForNewStep: string | undefined;
  previousStepIdForNewStep: string | undefined;
  stepToCopy: WorkflowConfigStepType | undefined;
  stepIdToDelete: string | undefined;
};

type WorkflowGraphStoreActions = {
  selectNode: (nodeId?: NodeId) => void;

  deselectNode: () => void;

  onNodesChange: OnNodesChange<
    WorkflowGraphNodeType | WorkflowGraphParallelNodeType
  >;

  onEdgesChange: OnEdgesChange<WorkflowGraphEdgeType>;

  setNodes: (
    nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>
  ) => void;

  setEdges: (edges: WorkflowGraphEdgeType[]) => void;

  onNodeHoverEnter: (nodeId: NodeId) => void;

  onNodeHoverLeave: (nodeId: NodeId) => void;

  setIsInEditMode: (isInEditMode: boolean) => void;

  setIsSelectingNodeToSourceFormDataFrom: (
    isSelectingNodeToSourceFormDataFrom: boolean
  ) => void;

  setNodeIdToSourceFormDataFrom: (nodeIdToSourceFormDataFrom: string) => void;

  setOriginalConfiguration: (configuration: WorkflowConfigurationType) => void;

  setOriginalTriggers: (triggers: WorkflowTriggerType[]) => void;

  setCurrentConfiguration: (configuration: WorkflowConfigurationType) => void;

  setCurrentTriggers: (triggers: WorkflowTriggerType[]) => void;

  setTemplateVariables: (variables: string[]) => void;

  setTaskVariables: (
    variables: Record<string, { input: string[]; output: string[] }>
  ) => void;

  setTriggerVariables: (variables: string[]) => void;

  setInitialVariables: (variables: string[]) => void;

  setPathMap: (pathMap: Record<string, string[]>) => void;

  setReversePathMap: (reversePathMap: Record<string, string[]>) => void;

  setIsFetchingConfiguration: (isFetching: boolean) => void;

  updateBaseInformationWithHistory: (
    stepId: NodeId,
    configuration:
      | Pick<
          WorkflowUserTaskType,
          "name" | "description" | "priority_id" | "is_dismissible"
        >
      | Pick<WorkflowSystemActionType, "name" | "description">
  ) => void;

  updateUserTaskAssigneeWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      WorkflowUserTaskType,
      "assignee_user" | "assignee_group" | "assignment_filter"
    >
  ) => void;

  updateUserTaskSubmitFormWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      FormSubmissionTaskType,
      "form_schema_id" | "form_required" | "auto_attach" | "autocomplete"
    >
  ) => void;

  updateUserTaskChoiceWithHistory: (
    stepId: NodeId,
    configuration: Pick<ChoiceTask, "choices">
  ) => void;

  updateFormChoiceAutomationWithHistory: (
    stepId: NodeId,
    configuration: Pick<FormChoiceAction, "choices" | "form_schema_id">
  ) => void;

  updateVariableChoiceAutomationWithHistory: (
    stepId: NodeId,
    configuration: Pick<VariableChoiceAction, "choices">
  ) => void;

  updateRunTemplatedReportWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      RunTemplatedReportAction,
      "name" | "templated_report_name" | "report_input" | "enable_new_version"
    >
  ) => void;

  updateRunWorkerWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      RunWorkerActionType,
      "worker_id" | "worker_version_id" | "worker_run_input"
    >
  ) => void;

  updateFormCreationAutomationWithHistory: (
    stepId: NodeId,
    configuration: Pick<CreateFormActionType, "form_schema_id" | "form_status">
  ) => void;

  updateTriggerDueDateWithHistory: (
    triggerIndex: number,
    configuration?: WorkflowTriggerDueDate
  ) => void;

  updateTriggerWorkflowCompletionWithHistory: (
    triggerIndex: number,
    configuration?: Pick<
      WorkflowTriggerWorkflowComplete,
      "workflow_template_id"
    >
  ) => void;

  updateFormCreationTriggerWithHistory: (
    triggerIndex: number,
    configuration?: Pick<
      WorkflowTriggerFormCreate,
      "answer_filter" | "form_schema_id"
    >
  ) => void;

  updateAssetCreateAndUpdateTriggerWithHistory: (
    triggerIndex: number,
    configuration?: Pick<
      WorkflowTriggerAssetCreate,
      "asset_filter" | "asset_type"
    > &
      Partial<Pick<WorkflowTriggerAssetUpdate, "attributes">>
  ) => void;

  updateScheduledTriggerWithHistory: (
    triggerIndex: number,
    configuration?: Pick<WorkflowTriggerScheduled, "cron">
  ) => void;

  updateOutputWithHistory: (
    stepId: NodeId,
    output: Record<string, string>
  ) => void;

  updateInputWithHistory: (
    stepId: NodeId,
    input: Record<string, string>
  ) => void;

  updatePluginWithHistory: (
    stepId: NodeId,
    configuration?: WorkflowPluginConfig
  ) => void;

  updateDelayWithHistory: (
    stepId: NodeId,
    configuration: Pick<DelayActionType, "time_period" | "duration">
  ) => void;

  updateRunCalculationsWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      RunCalculationsAction,
      "filter" | "year_months" | "promote_to_record" | "fill_year_months"
    >
  ) => void;

  updateCreateEventWithHistory: (
    stepId: NodeId,
    configuration: Pick<CreateEventAction, "event_schema_id" | "event_status">
  ) => void;

  isNodeDirty: (nodeId: string) => boolean;

  markNodeAsDirty: (nodeId: NodeId) => void;

  updateRunDRVCWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      RunDRVCActionType,
      "filter" | "overwrite_all_values" | "seed_records" | "fill_year_months"
    >
  ) => void;

  updateLockReportWithHistory: (
    stepId: NodeId,
    configuration: {
      templated_report_name?: string;
      input?: Record<string, string>;
      report_id?: string;
    }
  ) => void;

  updateUpdateRecordStatusWithHistory: (
    stepId: NodeId,
    configuration: UpdateRecordStatusFormValuesType
  ) => void;

  updateAssessTemplatedReportForCommentsWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      AssessTemplatedReportForCommentsAction,
      "report_id" | "header_name" | "styles_to_assess"
    >
  ) => void;

  updateUpdateAttributesWithHistory: (
    stepId: NodeId,
    configuration: Pick<UpdateAttributesActionType, "assets">
  ) => void;

  updateSendNotificationWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      SendNotificationActionType,
      "message" | "title" | "users" | "groups"
    >
  ) => void;

  updateSendReportWithHistory: (
    stepId: NodeId,
    configuration: Pick<
      SendReportActionType,
      "message" | "title" | "users" | "groups" | "report_filter" | "report_id"
    >
  ) => void;

  getAvailableStepVariables: (stepId: string) => string[];

  markNodeAsDirtyFromMove: (nodeId: NodeId) => void;

  revertTaskChanges: (stepId: NodeId) => void;

  revertAllChanges: () => void;

  onSuccessfulSave: () => void;

  resetState: () => void;

  setIsDummyNodeSelected: (isDummyNodeSelected: boolean) => void;

  setNextStepIdForNewStep: (nextStepIdForNewStep: string | undefined) => void;

  setPreviousStepIdForNewStep: (
    previousStepIdForNewStep: string | undefined
  ) => void;

  getConfigurationWithVisualizationMetadata: () => {
    configuration: WorkflowConfigurationType | undefined;
    triggers: WorkflowTriggerType[] | undefined;
  };

  setStepToCopy: (stepToCopy: WorkflowConfigStepType | undefined) => void;

  setStepIdToDelete: (stepIdToDelete: string | undefined) => void;
};

type WorkflowGraphStoreType = WorkflowGraphStoreState &
  WorkflowGraphStoreActions;

const initialState: WorkflowGraphStoreState = {
  nodes: [],

  edges: [],

  isInEditMode: false,

  isFetchingConfiguration: false,

  dirtyNodes: {},

  currentConfiguration: undefined,

  currentTriggers: undefined,

  originalConfiguration: undefined,

  originalTriggers: undefined,

  selectedNodeId: undefined,

  templateVariables: [],

  taskVariables: {},

  triggerVariables: [],

  initialVariables: [],

  pathMap: {},

  reversePathMap: {},
  isSelectingNodeToSourceFormDataFrom: false,
  nodeIdToSourceFormDataFrom: undefined,

  isDummyNodeSelected: false,

  nextStepIdForNewStep: undefined,

  previousStepIdForNewStep: undefined,

  stepToCopy: undefined,

  stepIdToDelete: undefined,
};

const useWorkflowGraphStore = create<WorkflowGraphStoreType>((set, get) => ({
  ...initialState,

  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },

  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },

  setNodes: (nodes) => {
    set({ nodes });
  },

  setEdges: (edges) => {
    set({ edges });
  },

  onNodeHoverEnter: (nodeId) => {
    set({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId)
          return produce(node, (draft) => {
            draft.data.isHovered = true;
          });
        else {
          return node;
        }
      }),
    });
  },

  onNodeHoverLeave: (nodeId) => {
    set({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId)
          return produce(node, (draft) => {
            draft.data.isHovered = false;
          });
        else {
          return node;
        }
      }),
    });
  },

  setIsInEditMode: (isInEditMode) => {
    set({ isInEditMode });
  },

  setIsSelectingNodeToSourceFormDataFrom: (
    isSelectingNodeToSourceFormDataFrom
  ) => {
    set({
      isSelectingNodeToSourceFormDataFrom,
      nodes: get().nodes.map((node) =>
        produce(node, (draft) => {
          if (isSelectingNodeToSourceFormDataFrom) {
            // Only disable nodes without output when in selection mode
            const allWorkflowSteps = flattenWorkflowSteps(
              get().currentConfiguration?.steps
            );
            const hasOutput =
              allWorkflowSteps[node.id]?.task?.output !== undefined;
            draft.data.isDisabled =
              node.id !== get().selectedNodeId && !hasOutput;
          } else {
            // Clear disabled state when exiting selection mode
            draft.data.isDisabled = false;
          }
        })
      ),
    });
  },

  setNodeIdToSourceFormDataFrom: (nodeIdToSourceFormDataFrom) => {
    if (!get().isSelectingNodeToSourceFormDataFrom) {
      set({ nodeIdToSourceFormDataFrom: undefined });
    } else {
      set({ nodeIdToSourceFormDataFrom });
    }
  },

  setOriginalConfiguration: (originalConfiguration) => {
    set({ originalConfiguration });
  },

  setOriginalTriggers: (originalTriggers) => {
    set({ originalTriggers });
  },

  setCurrentConfiguration: (currentConfiguration) => {
    set({ currentConfiguration });
  },

  setCurrentTriggers: (currentTriggers) => {
    set({ currentTriggers });
  },

  setIsFetchingConfiguration: (isFetchingConfiguration) => {
    set({ isFetchingConfiguration });
  },

  setTaskVariables: (taskVariables) => {
    set({ taskVariables });
  },

  setTriggerVariables: (triggerVariables) => {
    set({ triggerVariables });
  },

  setTemplateVariables: (templateVariables) => {
    set({ templateVariables });
  },

  setInitialVariables: (initialVariables) => {
    set({ initialVariables });
  },

  setPathMap: (pathMap) => {
    set({ pathMap });
  },

  setReversePathMap: (reversePathMap) => {
    set({ reversePathMap });
  },

  selectNode: (selectedNodeId) => {
    if (get().isSelectingNodeToSourceFormDataFrom) {
      set({
        nodeIdToSourceFormDataFrom: selectedNodeId,
      });
    } else {
      set({ selectedNodeId });
    }
  },

  deselectNode: () => {
    set({
      nodes: get().nodes.map((newNode) =>
        produce(newNode, (draft) => {
          draft.data.isTranslucent = false;
          draft.data.isDisabled = false;
          draft.data.isSelected = false;
        })
      ),
      edges: get().edges.map((newEdge) =>
        produce(newEdge, (draft) => {
          delete draft.data?.isDisabled;
          delete draft.data?.isSelected;
        })
      ),
      selectedNodeId: undefined,
      isSelectingNodeToSourceFormDataFrom: false,
      nodeIdToSourceFormDataFrom: undefined,
    });
  },

  updateBaseInformationWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        allWorkflowSteps[stepId].name = configuration.name;
        allWorkflowSteps[stepId].description = configuration.description;
        if (
          allWorkflowSteps[stepId].type === WorkflowStepType.USER_TASK &&
          "priority_id" in configuration
        ) {
          allWorkflowSteps[stepId].priority_id = configuration.priority_id;
        }
        if (
          allWorkflowSteps[stepId].type === WorkflowStepType.USER_TASK &&
          "is_dismissible" in configuration
        ) {
          allWorkflowSteps[stepId].is_dismissible =
            configuration.is_dismissible ?? false;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateUserTaskAssigneeWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (allWorkflowSteps[stepId].type === WorkflowStepType.USER_TASK) {
          allWorkflowSteps[stepId].assignee_user = configuration.assignee_user;
          allWorkflowSteps[stepId].assignee_group =
            configuration.assignee_group;
          allWorkflowSteps[stepId].assignment_filter =
            configuration.assignment_filter;
          // Legacy property on BE
          allWorkflowSteps[stepId].assignee =
            configuration.assignee_user ?? null;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateUserTaskSubmitFormWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (allWorkflowSteps[stepId].task.type === UserTaskType.SUBMIT_FORM) {
          allWorkflowSteps[stepId].task.form_schema_id =
            configuration.form_schema_id;
          allWorkflowSteps[stepId].task.form_required =
            configuration.form_required;
          allWorkflowSteps[stepId].task.auto_attach = configuration.auto_attach;
          allWorkflowSteps[stepId].task.autocomplete =
            configuration.autocomplete;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateUserTaskChoiceWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (allWorkflowSteps[stepId].task.type === UserTaskType.CHOICE) {
          allWorkflowSteps[stepId].task.choices = configuration.choices;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateFormChoiceAutomationWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.FORM_CHOICE
        ) {
          allWorkflowSteps[stepId].task.choices = configuration.choices;
          allWorkflowSteps[stepId].task.form_schema_id =
            configuration.form_schema_id;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateVariableChoiceAutomationWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.VARIABLE_CHOICE
        ) {
          allWorkflowSteps[stepId].task.choices = configuration.choices;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateRunTemplatedReportWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.RUN_TEMPLATED_REPORT
        ) {
          allWorkflowSteps[stepId].task.name = configuration.name;
          allWorkflowSteps[stepId].task.templated_report_name =
            configuration.templated_report_name;
          allWorkflowSteps[stepId].task.report_input =
            configuration.report_input;
          allWorkflowSteps[stepId].task.enable_new_version =
            configuration.enable_new_version;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateRunWorkerWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.RUN_WORKER
        ) {
          allWorkflowSteps[stepId].task.worker_id = configuration.worker_id;
          allWorkflowSteps[stepId].task.worker_version_id =
            configuration.worker_version_id;
          allWorkflowSteps[stepId].task.worker_run_input =
            configuration.worker_run_input;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateFormCreationAutomationWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.CREATE_FORM
        ) {
          allWorkflowSteps[stepId].task.form_schema_id =
            configuration.form_schema_id;
          allWorkflowSteps[stepId].task.form_status = configuration.form_status;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateTriggerDueDateWithHistory: (triggerIndex, configuration) => {
    const currentTriggers = get().currentTriggers;
    if (!currentTriggers) return;

    set({
      currentTriggers: produce(currentTriggers, (draft) => {
        if (!configuration) {
          delete draft[triggerIndex].due_date;
          return;
        }

        const cleanValues: WorkflowTriggerDueDate = pick(configuration, [
          "duration",
          "time_period",
        ]);

        if (configuration.start_variable) {
          cleanValues.start_variable = configuration.start_variable;
        }

        draft[triggerIndex].due_date = cleanValues;
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[getWorkflowConfigurationTriggerKeyFromIndex(triggerIndex)] = true;
      }),
    });
  },

  updateTriggerWorkflowCompletionWithHistory: (triggerIndex, configuration) => {
    const currentTriggers = get().currentTriggers;
    if (!currentTriggers) return;

    set({
      currentTriggers: produce(currentTriggers, (draft) => {
        if (
          draft[triggerIndex].type === "system" &&
          draft[triggerIndex].event === "workflow_complete" &&
          configuration?.workflow_template_id
        ) {
          (
            draft[triggerIndex] as WorkflowTriggerWorkflowComplete
          ).workflow_template_id = configuration.workflow_template_id;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[getWorkflowConfigurationTriggerKeyFromIndex(triggerIndex)] = true;
      }),
    });
  },

  updateAssetCreateAndUpdateTriggerWithHistory: (
    triggerIndex,
    configuration
  ) => {
    const currentTriggers = get().currentTriggers;
    if (!currentTriggers) return;

    set({
      currentTriggers: produce(currentTriggers, (draft) => {
        if (
          configuration &&
          (isTriggerAssetCreate(draft[triggerIndex]) ||
            isTriggerAssetUpdate(draft[triggerIndex]))
        ) {
          draft[triggerIndex].asset_filter = configuration.asset_filter;
          draft[triggerIndex].asset_type = configuration.asset_type;

          if (isTriggerAssetUpdate(draft[triggerIndex])) {
            draft[triggerIndex].attributes = configuration.attributes ?? [];
          }
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[getWorkflowConfigurationTriggerKeyFromIndex(triggerIndex)] = true;
      }),
    });
  },

  updateFormCreationTriggerWithHistory: (triggerIndex, configuration) => {
    const currentTriggers = get().currentTriggers;
    if (!currentTriggers) return;

    set({
      currentTriggers: produce(currentTriggers, (draft) => {
        if (
          configuration &&
          draft[triggerIndex].type === "system" &&
          draft[triggerIndex].event === "form_submission_create"
        ) {
          (draft[triggerIndex] as WorkflowTriggerFormCreate).form_schema_id =
            configuration.form_schema_id;

          if (configuration.answer_filter)
            (draft[triggerIndex] as WorkflowTriggerFormCreate).answer_filter =
              configuration.answer_filter;
          else {
            delete (draft[triggerIndex] as WorkflowTriggerFormCreate)
              .answer_filter;
          }
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[getWorkflowConfigurationTriggerKeyFromIndex(triggerIndex)] = true;
      }),
    });
  },

  updateScheduledTriggerWithHistory: (triggerIndex, configuration) => {
    const currentTriggers = get().currentTriggers;
    if (!currentTriggers) return;

    set({
      currentTriggers: produce(currentTriggers, (draft) => {
        if (configuration && draft[triggerIndex].type === "scheduled") {
          draft[triggerIndex].cron = configuration.cron;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[getWorkflowConfigurationTriggerKeyFromIndex(triggerIndex)] = true;
      }),
    });
  },

  updateOutputWithHistory: (stepId, output) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        allWorkflowSteps[stepId].task.output = output;
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateInputWithHistory: (stepId, input) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        flattenWorkflowSteps(draft.steps)[stepId].task.input = input;
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updatePluginWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const step = flattenWorkflowSteps(draft.steps)[stepId];
        if (step.type === "user_task") {
          step.task.plugin_name = configuration?.plugin_name;
          step.task.plugin_version = configuration?.plugin_version;
        }
        if (
          step.type === "system_action" &&
          (step.task.type === SystemActionType.CREATE_FORM ||
            step.task.type === SystemActionType.SEND_NOTIFICATION ||
            step.task.type === SystemActionType.SEND_REPORT ||
            step.task.type === SystemActionType.VARIABLE_CHOICE)
        ) {
          step.task.plugin_name = configuration?.plugin_name;
          step.task.plugin_version = configuration?.plugin_version;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  isNodeDirty: (nodeId) => !!get().dirtyNodes[nodeId],

  markNodeAsDirty: (nodeId) => {
    set({
      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[nodeId] = true;
      }),
    });
  },

  updateLockReportWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        const taskType = allWorkflowSteps[stepId].task.type;
        if (
          taskType === SystemActionType.LOCK_REPORT ||
          taskType === SystemActionType.UNLOCK_REPORT
        ) {
          // If report_id is provided, clear report_filter
          if (configuration.report_id) {
            allWorkflowSteps[stepId].task.report_id = configuration.report_id;
            delete allWorkflowSteps[stepId].task.report_filter;
          } else {
            // If report_filter data is provided, clear report_id and set report_filter
            allWorkflowSteps[stepId].task.report_filter = {
              ...allWorkflowSteps[stepId].task.report_filter,
              templated_report_name: configuration.templated_report_name,
              input: configuration.input,
            };
            delete allWorkflowSteps[stepId].task.report_id;
          }
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  getAvailableStepVariables(stepId: string): string[] {
    const parents = collectParentPathsForStep(stepId, get().reversePathMap);
    const variables = getAvailableVariablesForStep(
      parents,
      get().templateVariables,
      get().taskVariables,
      get().triggerVariables,
      get().initialVariables
    );
    return variables;
  },

  updateDelayWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (allWorkflowSteps[stepId].task.type === SystemActionType.DELAY) {
          allWorkflowSteps[stepId].task.duration = Number(
            configuration.duration
          );
          allWorkflowSteps[stepId].task.time_period = configuration.time_period;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateRunCalculationsWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.RUN_CALCULATIONS
        ) {
          allWorkflowSteps[stepId].task.filter = configuration.filter;
          allWorkflowSteps[stepId].task.year_months = configuration.year_months;
          allWorkflowSteps[stepId].task.promote_to_record =
            configuration.promote_to_record;
          allWorkflowSteps[stepId].task.fill_year_months =
            configuration.fill_year_months;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateUpdateRecordStatusWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.LOCK_RECORD ||
          allWorkflowSteps[stepId].task.type === SystemActionType.UNLOCK_RECORD
        ) {
          allWorkflowSteps[stepId].task.record_filter =
            configuration.record_filter;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateCreateEventWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.CREATE_EVENT
        ) {
          allWorkflowSteps[stepId].task.event_schema_id =
            configuration.event_schema_id;
          allWorkflowSteps[stepId].task.event_status =
            configuration.event_status;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateRunDRVCWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION
        ) {
          allWorkflowSteps[stepId].task.filter = configuration.filter;
          allWorkflowSteps[stepId].task.overwrite_all_values =
            configuration.overwrite_all_values;
          allWorkflowSteps[stepId].task.seed_records =
            configuration.seed_records;
          allWorkflowSteps[stepId].task.fill_year_months =
            configuration.fill_year_months;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateUpdateAttributesWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        const step = allWorkflowSteps[stepId];
        const task = step.task as {
          type: string;
          assets?: typeof configuration.assets;
        };
        const taskType = task.type;
        if (
          taskType === SystemActionType.UPDATE_ATTRIBUTES ||
          taskType === SystemActionType.UPDATE_CUSTOM_ATTRIBUTES
        ) {
          // update_custom_attributes is deprecated, convert it to update_attributes
          if (taskType === SystemActionType.UPDATE_CUSTOM_ATTRIBUTES) {
            task.type = SystemActionType.UPDATE_ATTRIBUTES;
          }
          task.assets = configuration.assets;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateAssessTemplatedReportForCommentsWithHistory: (
    stepId,
    configuration
  ) => {
    const currentConfiguration = get().currentConfiguration;

    if (!currentConfiguration) {
      return;
    }

    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS
        ) {
          allWorkflowSteps[stepId].task.report_id = configuration.report_id;
          allWorkflowSteps[stepId].task.header_name = configuration.header_name;
          allWorkflowSteps[stepId].task.styles_to_assess =
            configuration.styles_to_assess;
        }
      }),

      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateSendNotificationWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type ===
          SystemActionType.SEND_NOTIFICATION
        ) {
          allWorkflowSteps[stepId].task.message = configuration.message;
          allWorkflowSteps[stepId].task.title = configuration.title;
          allWorkflowSteps[stepId].task.users = configuration.users;
          allWorkflowSteps[stepId].task.groups = configuration.groups;
        }
      }),
      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  updateSendReportWithHistory: (stepId, configuration) => {
    const currentConfiguration = get().currentConfiguration;
    if (!currentConfiguration) return;
    set({
      currentConfiguration: produce(currentConfiguration, (draft) => {
        const allWorkflowSteps = flattenWorkflowSteps(draft.steps);
        if (
          allWorkflowSteps[stepId].task.type === SystemActionType.SEND_REPORT
        ) {
          allWorkflowSteps[stepId].task.message = configuration.message;
          allWorkflowSteps[stepId].task.title = configuration.title;
          allWorkflowSteps[stepId].task.users = configuration.users;
          allWorkflowSteps[stepId].task.groups = configuration.groups;
          allWorkflowSteps[stepId].task.report_filter =
            configuration.report_filter;
          allWorkflowSteps[stepId].task.report_id = configuration.report_id;
        }
      }),
      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[stepId] = true;
      }),
    });
  },

  markNodeAsDirtyFromMove: (nodeId) => {
    // Only mark non-finish nodes as dirty (config nodes and triggers)
    if (nodeId.endsWith("-end")) return;

    set({
      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        draft[nodeId] = true;
      }),
    });
  },

  revertTaskChanges: (stepId) => {
    const newChanges: Partial<WorkflowGraphStoreState> = {
      dirtyNodes: produce(get().dirtyNodes, (draft) => {
        delete draft[stepId];
      }),
    };

    const isTrigger = stepId.startsWith("trigger-");

    if (isTrigger) {
      const originalTriggers = get().originalTriggers;
      const currentTriggers = get().currentTriggers;
      if (!currentTriggers) return;
      const triggerIndex =
        getWorkflowConfigurationTriggerIndexFromTriggerKey(stepId);

      if (!originalTriggers || triggerIndex >= originalTriggers.length) {
        newChanges.currentTriggers = produce(currentTriggers, (draft) => {
          draft.splice(triggerIndex, 1);
        });
      } else {
        newChanges.currentTriggers = produce(currentTriggers, (draft) => {
          draft[triggerIndex] = originalTriggers[triggerIndex];
        });
      }
    } else {
      const originalConfiguration = get().originalConfiguration;
      const currentConfiguration = get().currentConfiguration;
      if (!currentConfiguration || !originalConfiguration) return;

      // Check if step exists in original configuration (including nested steps in parallel branches)
      const allOriginalSteps = getAllWorkflowSteps(originalConfiguration.steps);
      const originalStep = allOriginalSteps[stepId];

      if (!originalStep) {
        // Step doesn't exist in original - it was newly added, so delete it
        newChanges.currentConfiguration = produce(
          currentConfiguration,
          (draft) => {
            deleteStepFromWorkflow(draft, stepId);
          }
        );
      } else {
        // Step exists in original - restore it to its original state
        newChanges.currentConfiguration = produce(
          currentConfiguration,
          (draft) => {
            // Check if step is top-level or in a branch
            if (draft.steps[stepId]) {
              // Top-level step - restore directly
              draft.steps[stepId] = cloneDeep(originalStep);
            } else {
              // Step is in a branch - find the branch and restore it there
              const branchInfo = findBranchContainingStep(draft, stepId);
              if (branchInfo) {
                branchInfo.branch.steps[stepId] = cloneDeep(originalStep);
              }
            }
          }
        );
      }
    }

    set(newChanges);
  },

  revertAllChanges: () => {
    const newChanges: Partial<WorkflowGraphStoreState> = {
      dirtyNodes: {},
    };

    const originalConfiguration = get().originalConfiguration;

    if (originalConfiguration) {
      newChanges.currentConfiguration = cloneDeep(originalConfiguration);
    }

    const originalTriggers = get().originalTriggers;
    newChanges.currentTriggers = originalTriggers
      ? cloneDeep(originalTriggers)
      : [];

    set(newChanges);
  },

  onSuccessfulSave: () => {
    // TODO: Improve this logic. Refetch the template after it's saved:
    const originalConfiguration = get().originalConfiguration;
    const currentConfiguration = get().currentConfiguration;
    const currentTriggers = get().currentTriggers;
    if (!currentConfiguration || !originalConfiguration) return;
    set({
      originalConfiguration: cloneDeep(currentConfiguration),
      originalTriggers: currentTriggers
        ? cloneDeep(currentTriggers)
        : undefined,
      dirtyNodes: {},
    });
  },

  getConfigurationWithVisualizationMetadata: () => {
    const currentConfiguration = get().currentConfiguration;
    const currentTriggers = get().currentTriggers;
    const nodes = get().nodes;

    const filteredTriggers =
      currentTriggers?.filter(
        (trigger) => trigger.type !== WorkflowTriggerDisplayType.DUMMY
      ) ?? [];
    if (!currentConfiguration || !filteredTriggers) {
      return {
        configuration: currentConfiguration,
        triggers: filteredTriggers,
      };
    }

    // Create a map of node IDs to their positions
    const nodePositions = new Map<string, { x: number; y: number }>();
    nodes.forEach((node) => {
      nodePositions.set(node.id, {
        x: node.position.x,
        y: node.position.y,
      });
    });

    // Helper function to recursively enrich steps with visualization_metadata
    const enrichStepsWithVisualizationMetadata = (
      steps: Record<string, WorkflowConfigurationType["steps"][string]>
    ) => {
      Object.entries(steps).forEach(([stepId, step]) => {
        const position = nodePositions.get(stepId);
        if (position && (position.x !== 0 || position.y !== 0)) {
          step.visualization_metadata = {
            x: position.x,
            y: position.y,
          };
        } else {
          // Remove visualization_metadata if position is (0, 0)
          delete step.visualization_metadata;
        }

        // Recursively handle parallel steps
        if (step.type === WorkflowStepType.PARALLEL) {
          step.branches.forEach((branch) => {
            enrichStepsWithVisualizationMetadata(branch.steps);
          });
        }
      });
    };

    // Enrich configuration steps with visualization_metadata
    const enrichedConfiguration = produce(currentConfiguration, (draft) => {
      enrichStepsWithVisualizationMetadata(draft.steps);
    });

    // Enrich triggers with visualization_metadata
    const enrichedTriggers = currentTriggers
      ?.filter((trigger) => trigger.type !== WorkflowTriggerDisplayType.DUMMY)
      ?.map((trigger, index) => {
        const triggerKey = getWorkflowConfigurationTriggerKeyFromIndex(index);
        const position = nodePositions.get(triggerKey);

        if (position && (position.x !== 0 || position.y !== 0)) {
          return {
            ...trigger,
            visualization_metadata: {
              x: position.x,
              y: position.y,
            },
          };
        } else {
          // Remove visualization_metadata if position is (0, 0)
          const { visualization_metadata: _, ...rest } = trigger;
          return rest as WorkflowTriggerType;
        }
      });

    return {
      configuration: enrichedConfiguration,
      triggers: enrichedTriggers,
    };
  },

  resetState: () => {
    set(initialState);
  },

  setIsDummyNodeSelected: (isDummyNodeSelected) => {
    set({ isDummyNodeSelected });
  },

  setNextStepIdForNewStep: (nextStepIdForNewStep) => {
    set({ nextStepIdForNewStep });
  },

  setPreviousStepIdForNewStep: (previousStepIdForNewStep) => {
    set({ previousStepIdForNewStep });
  },

  setStepToCopy: (stepToCopy) => {
    set({ stepToCopy });
  },

  setStepIdToDelete: (stepIdToDelete) => {
    set({ stepIdToDelete });
  },
}));

// Split selectors by functionality
const useWorkflowGraphNodesSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      nodes: state.nodes.map((node) =>
        produce(node, (draft) => {
          draft.data.isDirty = !!state.dirtyNodes[node.id];
        })
      ),
      edges: state.edges,
      onNodesChange: state.onNodesChange,
      onEdgesChange: state.onEdgesChange,
      setNodes: state.setNodes,
      setEdges: state.setEdges,
      onNodeHoverEnter: state.onNodeHoverEnter,
      onNodeHoverLeave: state.onNodeHoverLeave,
      isNodeDirty: (nodeId: NodeId) => !!state.dirtyNodes[nodeId],
      markNodeAsDirty: state.markNodeAsDirty,
      isDummyNodeSelected: state.isDummyNodeSelected,
      setIsDummyNodeSelected: state.setIsDummyNodeSelected,
      markNodeAsDirtyFromMove: state.markNodeAsDirtyFromMove,
    }))
  );

const useWorkflowGraphSelectionSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      selectedNodeId: state.selectedNodeId,
      selectedNode: state.nodes.find(
        (node) => node.id === state.selectedNodeId
      ),
      selectNode: state.selectNode,
      deselectNode: state.deselectNode,
      isSelectingNodeToSourceFormDataFrom:
        state.isSelectingNodeToSourceFormDataFrom,
      setIsSelectingNodeToSourceFormDataFrom:
        state.setIsSelectingNodeToSourceFormDataFrom,
      nodeIdToSourceFormDataFrom: state.nodeIdToSourceFormDataFrom,
      setNodeIdToSourceFormDataFrom: state.setNodeIdToSourceFormDataFrom,
      stepToCopy: state.stepToCopy,
      setStepToCopy: state.setStepToCopy,
      stepIdToDelete: state.stepIdToDelete,
      setStepIdToDelete: state.setStepIdToDelete,
    }))
  );

const useWorkflowGraphEditModeSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      isInEditMode: state.isInEditMode,
      setIsInEditMode: state.setIsInEditMode,
    }))
  );

const useWorkflowGraphConfigurationSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      isFetchingConfiguration: state.isFetchingConfiguration,
      originalConfiguration: state.originalConfiguration,
      originalTriggers: state.originalTriggers,
      currentConfiguration: state.currentConfiguration,
      currentTriggers: state.currentTriggers,
      setOriginalConfiguration: state.setOriginalConfiguration,
      setOriginalTriggers: state.setOriginalTriggers,
      setCurrentConfiguration: state.setCurrentConfiguration,
      setCurrentTriggers: state.setCurrentTriggers,
      setIsFetchingConfiguration: state.setIsFetchingConfiguration,
      pendingChangesCount: Object.keys(state.dirtyNodes).length,
      getConfigurationWithVisualizationMetadata:
        state.getConfigurationWithVisualizationMetadata,
    }))
  );

const useWorkflowGraphVariablesSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      templateVariables: state.templateVariables,
      taskVariables: state.taskVariables,
      triggerVariables: state.triggerVariables,
      setTriggerVariables: state.setTriggerVariables,
      initialVariables: state.initialVariables,
      setInitialVariables: state.setInitialVariables,
      pathMap: state.pathMap,
      reversePathMap: state.reversePathMap,
      setTemplateVariables: state.setTemplateVariables,
      setTaskVariables: state.setTaskVariables,
      setPathMap: state.setPathMap,
      setReversePathMap: state.setReversePathMap,
      getAvailableStepVariables: state.getAvailableStepVariables,
      nextStepIdForNewStep: state.nextStepIdForNewStep,
      setNextStepIdForNewStep: state.setNextStepIdForNewStep,
      previousStepIdForNewStep: state.previousStepIdForNewStep,
      setPreviousStepIdForNewStep: state.setPreviousStepIdForNewStep,
    }))
  );

const useWorkflowGraphUpdateActionsSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      updateBaseInformationWithHistory: state.updateBaseInformationWithHistory,
      updateUserTaskAssigneeWithHistory:
        state.updateUserTaskAssigneeWithHistory,
      updateUserTaskSubmitFormWithHistory:
        state.updateUserTaskSubmitFormWithHistory,
      updateUserTaskChoiceWithHistory: state.updateUserTaskChoiceWithHistory,
      updateFormChoiceAutomationWithHistory:
        state.updateFormChoiceAutomationWithHistory,
      updateVariableChoiceAutomationWithHistory:
        state.updateVariableChoiceAutomationWithHistory,
      updateRunTemplatedReportWithHistory:
        state.updateRunTemplatedReportWithHistory,
      updateRunWorkerWithHistory: state.updateRunWorkerWithHistory,
      updateFormCreationAutomationWithHistory:
        state.updateFormCreationAutomationWithHistory,
      updateTriggerDueDateWithHistory: state.updateTriggerDueDateWithHistory,
      updateTriggerWorkflowCompletionWithHistory:
        state.updateTriggerWorkflowCompletionWithHistory,
      updateFormCreationTriggerWithHistory:
        state.updateFormCreationTriggerWithHistory,
      updateAssetCreateAndUpdateTriggerWithHistory:
        state.updateAssetCreateAndUpdateTriggerWithHistory,
      updateScheduledTriggerWithHistory:
        state.updateScheduledTriggerWithHistory,
      updateOutputWithHistory: state.updateOutputWithHistory,
      updateInputWithHistory: state.updateInputWithHistory,
      updatePluginWithHistory: state.updatePluginWithHistory,
      updateDelayWithHistory: state.updateDelayWithHistory,
      updateRunCalculationsWithHistory: state.updateRunCalculationsWithHistory,
      updateCreateEventWithHistory: state.updateCreateEventWithHistory,
      updateRunDRVCWithHistory: state.updateRunDRVCWithHistory,
      updateLockReportWithHistory: state.updateLockReportWithHistory,
      updateUpdateRecordStatusWithHistory:
        state.updateUpdateRecordStatusWithHistory,
      updateAssessTemplatedReportForCommentsWithHistory:
        state.updateAssessTemplatedReportForCommentsWithHistory,
      updateUpdateAttributesWithHistory:
        state.updateUpdateAttributesWithHistory,
      updateSendNotificationWithHistory:
        state.updateSendNotificationWithHistory,
      updateSendReportWithHistory: state.updateSendReportWithHistory,
    }))
  );

const useWorkflowGraphHistoryActionsSelector = () =>
  useWorkflowGraphStore(
    useShallow((state) => ({
      revertTaskChanges: state.revertTaskChanges,
      revertAllChanges: state.revertAllChanges,
      onSuccessfulSave: state.onSuccessfulSave,
      resetState: state.resetState,
    }))
  );

// Main hook that combines all selectors for backward compatibility
export const useWorkflowGraphState = () => {
  const nodes = useWorkflowGraphNodesSelector();
  const selection = useWorkflowGraphSelectionSelector();
  const editMode = useWorkflowGraphEditModeSelector();
  const configuration = useWorkflowGraphConfigurationSelector();
  const variables = useWorkflowGraphVariablesSelector();
  const updateActions = useWorkflowGraphUpdateActionsSelector();
  const historyActions = useWorkflowGraphHistoryActionsSelector();

  return {
    ...nodes,
    ...selection,
    ...editMode,
    ...configuration,
    ...variables,
    ...updateActions,
    ...historyActions,
  };
};

// Export individual selectors for components that only need specific parts
export {
  useWorkflowGraphConfigurationSelector,
  useWorkflowGraphEditModeSelector,
  useWorkflowGraphHistoryActionsSelector,
  useWorkflowGraphNodesSelector,
  useWorkflowGraphSelectionSelector,
  useWorkflowGraphUpdateActionsSelector,
  useWorkflowGraphVariablesSelector,
};
